import { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import api from "../lib/api";
import { Loading, Avatar, StatTile } from "../components/common";
import DartPad from "../components/DartPad";
import Burst from "../components/Burst";
import { applyDart, resolveDart, checkoutSuggestion, dartLabel, START_501 } from "../lib/dartEngine";
import { coachTip } from "../lib/checkout";
import { playCheer } from "../lib/fx";
import { toast } from "sonner";
import { Home, Flag, X, Undo2, Trophy, Target, CheckCircle2, Dices } from "lucide-react";

const ATW_TARGETS = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, "BULL"];

function sessionStats(legs, currentTurns) {
  const allTurns = [...legs.flatMap((l) => l.turns), ...currentTurns];
  let darts = 0,
    points = 0,
    c180 = 0,
    highTurn = 0,
    highCo = 0;
  allTurns.forEach((t) => {
    darts += t.darts_used || 0;
    points += t.turn_score || 0;
    if (!t.busted) {
      highTurn = Math.max(highTurn, t.turn_score || 0);
      if (t.turn_score === 180) c180 += 1;
      if (t.is_checkout) highCo = Math.max(highCo, t.turn_score || 0);
    }
  });
  const wonLegDarts = legs.map((l) =>
    l.turns.filter((t) => t.player_id === l.winner_id).reduce((s, t) => s + (t.darts_used || 0), 0)
  );
  return {
    darts,
    points,
    avg: darts ? +(points / darts * 3).toFixed(1) : 0,
    c180,
    highTurn,
    highCo,
    legs: legs.length,
    bestLeg: wonLegDarts.length ? Math.min(...wonLegDarts) : 0,
  };
}

export default function PracticeLive() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [session, setSession] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        setSession((await api.get(`/practice/${id}`)).data);
      } catch {
        toast.error("Session not found");
        navigate("/practice");
      }
    })();
  }, [id, navigate]);

  if (!session) return <Loading label="Loading session" />;
  const gt = session.game_type;
  if (session.status === "completed") {
    if (gt === "around_the_world") return <PracticeSummaryATW session={session} navigate={navigate} />;
    if (gt === "baseball") return <PracticeSummaryBaseball session={session} navigate={navigate} />;
    if (gt === "nine_dart") return <NineDartSummary session={session} navigate={navigate} />;
    if (gt === "random_target") return <RandomTargetSummary session={session} navigate={navigate} />;
    return <PracticeSummary session={session} navigate={navigate} />;
  }
  if (gt === "around_the_world")
    return <PracticeATW session={session} setSession={setSession} navigate={navigate} />;
  if (gt === "baseball")
    return <PracticeBaseball session={session} setSession={setSession} navigate={navigate} />;
  if (gt === "nine_dart")
    return <NineDartBoard session={session} setSession={setSession} navigate={navigate} />;
  if (gt === "random_target")
    return <RandomTargetBoard session={session} setSession={setSession} navigate={navigate} />;
  return <PracticeBoard session={session} setSession={setSession} navigate={navigate} />;
}

const NINE_DART_MAX = 540;

function NineDartBoard({ session, setSession, navigate }) {
  const [darts, setDarts] = useState([]);
  const [saving, setSaving] = useState(false);
  const pb = session.personal_best || 0;

  const total = darts.reduce((s, d) => s + d.value, 0);
  const roundTotals = [0, 1, 2].map((r) =>
    darts.slice(r * 3, r * 3 + 3).reduce((s, d) => s + d.value, 0)
  );
  const dartNo = darts.length;
  const currentRound = Math.min(Math.floor(dartNo / 3) + 1, 3);

  const finish = async (finalDarts) => {
    const rt = [0, 1, 2].map((r) => finalDarts.slice(r * 3, r * 3 + 3).reduce((s, d) => s + d.value, 0));
    const tot = finalDarts.reduce((s, d) => s + d.value, 0);
    setSaving(true);
    try {
      const { data } = await api.put(`/practice/${session.id}`, {
        legs: [{ darts: finalDarts, round_totals: rt, total: tot }],
        status: "completed",
      });
      setSession(data);
    } catch {
      toast.error("Could not save session");
      setSaving(false);
    }
  };

  const onDart = (base, forceMult) => {
    if (darts.length >= 9 || saving) return;
    const m = base === 0 ? 1 : forceMult || 1;
    const value = base === 0 ? 0 : base * m;
    const label = base === 0 ? "MISS" : base === 25 ? (m === 2 ? "BULL" : "25") : (m === 3 ? "T" : m === 2 ? "D" : "") + base;
    const next = [...darts, { base, multiplier: m, value, label }];
    setDarts(next);
    if (next.length === 9) finish(next);
  };

  const undo = () => {
    if (darts.length && !saving) setDarts(darts.slice(0, -1));
  };

  return (
    <div className="max-w-xl mx-auto">
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#FF3B30] flex items-center gap-1.5">
            <Target className="w-4 h-4" /> 9-Dart High Score
          </div>
          <div className="font-display text-3xl font-black uppercase text-white leading-none">
            {session.player_name}
          </div>
        </div>
        <button
          data-testid="ninedart-exit-btn"
          onClick={() => navigate("/practice")}
          className="flex items-center gap-1.5 text-sm font-bold uppercase text-slate-400 hover:text-[#FF3B30] transition-colors"
        >
          <Flag className="w-4 h-4" /> Exit
        </button>
      </div>

      <div className="panel rounded-sm p-4 mb-3 text-center">
        <div data-testid="ninedart-progress" className="text-[10px] font-bold uppercase tracking-[0.15em] text-slate-500">
          Dart {Math.min(dartNo + 1, 9)} / 9 · Round {currentRound}
        </div>
        <div data-testid="ninedart-score" className="font-display text-6xl md:text-7xl font-black text-white leading-none">
          {total}
        </div>
        <div className="text-[10px] font-bold uppercase tracking-[0.15em] text-slate-500 mt-1">
          Running Score · Score to Beat <span className="text-[#FFCC00]">{pb}</span> / {NINE_DART_MAX} max
        </div>
      </div>

      <div className="panel rounded-sm p-3 mb-3 grid grid-cols-3 gap-2">
        {[0, 1, 2].map((r) => (
          <div key={r} className="text-center">
            <div className="text-[10px] font-bold uppercase tracking-[0.15em] text-slate-500">Round {r + 1}</div>
            <div
              data-testid={`ninedart-round-${r + 1}`}
              className={`font-display text-2xl font-black ${currentRound === r + 1 && dartNo < 9 ? "text-[#007AFF]" : "text-white"}`}
            >
              {roundTotals[r]}
            </div>
          </div>
        ))}
      </div>

      <div className="flex items-center justify-center gap-2 mb-3 h-9">
        {[0, 1, 2].map((i) => {
          const d = darts[(currentRound - 1) * 3 + i];
          return (
            <div
              key={i}
              className={`min-w-[64px] px-3 py-1.5 rounded-sm text-center font-display text-lg font-bold border ${
                d ? "border-[#007AFF] text-white" : "border-[#262626] text-slate-700"
              }`}
            >
              {d ? d.label : "—"}
            </div>
          );
        })}
      </div>

      <DartPad onDart={onDart} onUndo={undo} />
    </div>
  );
}

function NineDartSummary({ session, navigate }) {
  const leg = (session.legs || [])[0] || {};
  const rt = leg.round_totals || [0, 0, 0];
  const total = leg.total || 0;
  const ach = session.achievement || {};
  const isPB = ach.is_personal_best;
  const prevBest = ach.previous_best || 0;
  const bestRound = rt.length ? Math.max(...rt) : 0;
  const avg3 = (total / 3).toFixed(1);
  const [again, setAgain] = useState(false);

  const playAgain = async () => {
    setAgain(true);
    try {
      const { data } = await api.post("/practice", { player_id: session.player_id, game_type: "nine_dart" });
      navigate(`/practice/${data.id}`);
    } catch {
      toast.error("Could not start a new session");
      setAgain(false);
    }
  };

  return (
    <div className="max-w-xl mx-auto">
      <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} className="text-center py-6">
        <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-[#FF3B30] text-white font-bold uppercase text-xs tracking-widest rounded-sm mb-4">
          <Target className="w-4 h-4" /> 9-Dart High Score
        </div>
        <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500">Final Score</div>
        <h1 data-testid="ninedart-final" className="font-display text-7xl font-black uppercase tracking-tighter text-white leading-none">
          {total}
        </h1>
        {isPB ? (
          <div data-testid="ninedart-pb-badge" className="inline-flex items-center gap-1.5 mt-3 px-3 py-1 bg-[#FFCC00] text-black font-bold uppercase text-xs tracking-widest rounded-sm">
            <Trophy className="w-4 h-4" /> New Personal Best!
          </div>
        ) : (
          <div data-testid="ninedart-pb-badge" className="text-sm text-slate-400 mt-3 font-bold uppercase tracking-wide">
            Personal Best: <span className="text-[#FFCC00]">{Math.max(prevBest, total)}</span>
          </div>
        )}
      </motion.div>

      <div className="panel rounded-sm p-5">
        <div className="grid grid-cols-3 gap-2 mb-4">
          {[0, 1, 2].map((r) => (
            <div key={r} className="text-center">
              <div className="text-[10px] font-bold uppercase tracking-[0.15em] text-slate-500">Round {r + 1}</div>
              <div data-testid={`ninedart-summary-round-${r + 1}`} className="font-display text-3xl font-black text-white">
                {rt[r] ?? 0}
              </div>
            </div>
          ))}
        </div>
        <div className="grid grid-cols-2 gap-2">
          <StatTile testid="ninedart-best-round" label="Best 3-Dart Round" value={bestRound} accent="text-[#34C759]" />
          <StatTile testid="ninedart-avg" label="3-Dart Average" value={avg3} accent="text-[#007AFF]" />
        </div>
      </div>

      <div className="flex flex-col sm:flex-row gap-2 mt-6">
        <button
          data-testid="play-again-btn"
          onClick={playAgain}
          disabled={again}
          className="flex-1 bg-[#34C759] hover:bg-[#2fb350] disabled:opacity-50 text-black font-bold uppercase tracking-wide py-4 rounded-sm transition-colors"
        >
          {again ? "Starting..." : "Play Again"}
        </button>
        <button
          data-testid="back-to-practice-btn"
          onClick={() => navigate("/practice")}
          className="flex-1 flex items-center justify-center gap-2 panel rounded-sm py-4 font-bold uppercase tracking-wide text-white hover:border-[#007AFF] transition-colors"
        >
          <Home className="w-5 h-5" /> Back to Practice
        </button>
      </div>
    </div>
  );
}

const RTC_TOTAL = 30;
const RTC_DIFF_LABELS = { easy: "Easy", normal: "Normal", hard: "Hard" };

function buildRtcPool(difficulty) {
  const singles = [];
  for (let n = 1; n <= 20; n++) singles.push({ base: n, mult: 1, label: `${n}` });
  const doubles = [];
  for (let n = 1; n <= 20; n++) doubles.push({ base: n, mult: 2, label: `D${n}` });
  const triples = [];
  for (let n = 1; n <= 20; n++) triples.push({ base: n, mult: 3, label: `T${n}` });
  const bull = { base: 25, mult: 2, label: "BULL" };
  if (difficulty === "easy") return singles;
  if (difficulty === "normal") return [...singles, ...doubles, bull]; // bull ~occasional
  return [...singles, ...doubles, ...triples, bull]; // hard
}

function nextRtcTarget(pool, prev) {
  if (pool.length <= 1) return pool[0];
  let t = pool[Math.floor(Math.random() * pool.length)];
  let guard = 0;
  while (prev && t.base === prev.base && t.mult === prev.mult && guard < 12) {
    t = pool[Math.floor(Math.random() * pool.length)];
    guard += 1;
  }
  return t;
}

const rtcIsHit = (target, base, mult) => {
  if (base === 0) return false;
  if (target.base === 25) return base === 25 && mult === 2; // Bull: exact only
  if (target.mult === 1) return base === target.base; // plain number: any S/D/T of it
  return base === target.base && mult === target.mult; // explicit double/triple
};

function RandomTargetBoard({ session, setSession, navigate }) {
  const difficulty = session.difficulty || "easy";
  const pool = useMemo(() => buildRtcPool(difficulty), [difficulty]);
  const [results, setResults] = useState([]); // { target, base, mult, label, hit }
  const [target, setTarget] = useState(() => nextRtcTarget(buildRtcPool(difficulty), null));
  const [saving, setSaving] = useState(false);

  const thrown = results.length;
  const hits = results.filter((r) => r.hit).length;
  const misses = thrown - hits;
  const accuracy = thrown ? (hits / thrown) * 100 : 0;
  const { bestStreak, curStreak } = useMemo(() => {
    let best = 0, run = 0;
    results.forEach((r) => {
      run = r.hit ? run + 1 : 0;
      best = Math.max(best, run);
    });
    return { bestStreak: best, curStreak: run };
  }, [results]);

  const finish = async (finalResults) => {
    const h = finalResults.filter((r) => r.hit).length;
    let best = 0, run = 0;
    finalResults.forEach((r) => { run = r.hit ? run + 1 : 0; best = Math.max(best, run); });
    const count = (pred) => finalResults.filter((r) => r.hit && pred(r.target)).length;
    const rtc = {
      difficulty,
      targets: finalResults.map((r) => ({ base: r.target.base, mult: r.target.mult, label: r.target.label })),
      results: finalResults.map((r) => ({ base: r.base, mult: r.mult, label: r.label, hit: r.hit })),
      hits: h,
      misses: finalResults.length - h,
      accuracy: +(h / finalResults.length * 100).toFixed(1),
      best_streak: best,
      singles_hit: count((t) => t.mult === 1 && t.base !== 25),
      doubles_hit: count((t) => t.mult === 2 && t.base !== 25),
      triples_hit: count((t) => t.mult === 3),
      bull_hits: count((t) => t.base === 25 && t.mult === 2),
    };
    setSaving(true);
    try {
      const { data } = await api.put(`/practice/${session.id}`, { rtc, status: "completed" });
      setSession(data);
    } catch {
      toast.error("Could not save session");
      setSaving(false);
    }
  };

  const onDart = (base, forceMult) => {
    if (thrown >= RTC_TOTAL || saving) return;
    const mult = base === 0 ? 1 : forceMult || 1;
    const hit = rtcIsHit(target, base, mult);
    const label = dartLabel(base, mult);
    const next = [...results, { target, base, mult, label, hit }];
    setResults(next);
    if (next.length === RTC_TOTAL) {
      finish(next);
      return;
    }
    setTarget(nextRtcTarget(pool, target));
  };

  const undo = () => {
    if (!results.length || saving) return;
    const last = results[results.length - 1];
    setResults(results.slice(0, -1));
    setTarget(last.target);
  };

  const dartNo = Math.min(thrown + 1, RTC_TOTAL);

  return (
    <div className="max-w-xl mx-auto">
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#4ADE80] flex items-center gap-1.5">
            <Dices className="w-4 h-4" /> Random Target · {RTC_DIFF_LABELS[difficulty]}
          </div>
          <div className="font-display text-3xl font-black uppercase text-white leading-none">
            {session.player_name}
          </div>
        </div>
        <button
          data-testid="rtc-exit-btn"
          onClick={() => navigate("/practice")}
          className="flex items-center gap-1.5 text-sm font-bold uppercase text-slate-400 hover:text-[#FF3B30] transition-colors"
        >
          <Flag className="w-4 h-4" /> Exit
        </button>
      </div>

      <div className="panel rounded-sm p-6 mb-3 text-center border-t-2 border-t-[#4ADE80]">
        <div data-testid="rtc-progress-count" className="text-[10px] font-bold uppercase tracking-[0.15em] text-slate-500">
          Dart {dartNo} / {RTC_TOTAL}
        </div>
        <div className="text-[10px] font-bold uppercase tracking-[0.15em] text-slate-500 mt-2">Hit this target</div>
        <div data-testid="rtc-target" className="font-display text-7xl md:text-8xl font-black text-white leading-none">
          {target.label}
        </div>
      </div>

      <div className="grid grid-cols-4 gap-1 mb-3">
        <StatTile testid="rtc-hits" label="Hits" value={hits} accent="text-[#34C759]" />
        <StatTile testid="rtc-misses" label="Misses" value={misses} accent="text-[#FF3B30]" />
        <StatTile testid="rtc-accuracy" label="Accuracy" value={`${accuracy.toFixed(1)}%`} accent="text-[#007AFF]" />
        <StatTile testid="rtc-streak" label="Streak" value={`${curStreak}/${bestStreak}`} accent="text-[#FFCC00]" />
      </div>

      <div className="flex items-center justify-center gap-1.5 mb-3 h-8" data-testid="rtc-last-dart">
        {results.length > 0 ? (
          <>
            <span className="text-[10px] font-bold uppercase tracking-[0.15em] text-slate-500">Last</span>
            <span className="font-display text-base font-bold text-white">{results[results.length - 1].label}</span>
            <span className="text-slate-600">→</span>
            <span className={`font-display text-base font-black ${results[results.length - 1].hit ? "text-[#34C759]" : "text-[#FF3B30]"}`}>
              {results[results.length - 1].hit ? "HIT" : "MISS"}
            </span>
          </>
        ) : (
          <span className="text-[11px] text-slate-600 uppercase tracking-wide">Record the dart you actually threw</span>
        )}
      </div>

      <DartPad onDart={onDart} onUndo={undo} />
    </div>
  );
}

function RandomTargetSummary({ session, navigate }) {
  const s = session.stats || session.rtc || {};
  const cmp = session.rtc_compare || {};
  const difficulty = session.difficulty || s.difficulty || "easy";
  const [again, setAgain] = useState(false);

  const playAgain = async () => {
    setAgain(true);
    try {
      const { data } = await api.post("/practice", { player_id: session.player_id, game_type: "random_target", difficulty });
      navigate(`/practice/${data.id}`);
    } catch {
      toast.error("Could not start a new session");
      setAgain(false);
    }
  };

  const change = cmp.change;
  const changeStr = change == null ? "—" : change > 0 ? `+${change} ↑` : change < 0 ? `${change} ↓` : "0";
  const changeColor = change == null ? "text-slate-400" : change > 0 ? "text-[#34C759]" : change < 0 ? "text-[#FF3B30]" : "text-slate-300";

  return (
    <div className="max-w-2xl mx-auto">
      <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} className="text-center py-6">
        <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-[#4ADE80] text-black font-bold uppercase text-xs tracking-widest rounded-sm mb-4">
          <Dices className="w-4 h-4" /> Random Target · {RTC_DIFF_LABELS[difficulty]}
        </div>
        <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500">Accuracy</div>
        <h1 data-testid="rtc-final-accuracy" className="font-display text-7xl font-black uppercase tracking-tighter text-white leading-none">
          {Number(s.accuracy ?? 0).toFixed(1)}%
        </h1>
        <div data-testid="rtc-final-hits" className="font-display text-2xl font-bold text-[#34C759] mt-1">
          {s.hits ?? 0} / {RTC_TOTAL} hits
        </div>
      </motion.div>

      <div className="panel rounded-sm p-5 mb-4">
        <div className="grid grid-cols-3 gap-2 mb-3">
          <StatTile testid="rtc-sum-prev" label="Previous" value={cmp.previous != null ? `${cmp.previous}%` : "—"} accent="text-slate-300" />
          <StatTile testid="rtc-sum-change" label="Change" value={changeStr} accent={changeColor} />
          <StatTile testid="rtc-sum-last5" label="Last 5 Avg" value={`${cmp.last5 ?? 0}%`} accent="text-[#007AFF]" />
        </div>
        <StatTile testid="rtc-sum-pb" label={`Personal Best (${RTC_DIFF_LABELS[difficulty]})`} value={`${cmp.pb ?? 0}%`} accent="text-[#FFCC00]" />
      </div>

      <div className="panel rounded-sm p-6">
        <div className="space-y-1">
          {[
            ["Hits", `${s.hits ?? 0} / ${RTC_TOTAL}`],
            ["Misses", s.misses ?? 0],
            ["Best Hit Streak", s.best_streak ?? 0],
            ["Singles Hit", s.singles_hit ?? 0],
            ["Doubles Hit", s.doubles_hit ?? 0],
            ["Triples Hit", s.triples_hit ?? 0],
            ["Bull Hits", s.bull_hits ?? 0],
          ].map(([k, v]) => (
            <div key={k} className="flex items-center justify-between text-sm border-b border-[#1c1c1c] py-1.5">
              <span className="text-slate-400 font-medium">{k}</span>
              <span className="font-display text-lg font-bold text-white">{v}</span>
            </div>
          ))}
        </div>
      </div>

      <div className="flex flex-col sm:flex-row gap-2 mt-6">
        <button
          data-testid="play-again-btn"
          onClick={playAgain}
          disabled={again}
          className="flex-1 bg-[#34C759] hover:bg-[#2fb350] disabled:opacity-50 text-black font-bold uppercase tracking-wide py-4 rounded-sm transition-colors"
        >
          {again ? "Starting..." : "Play Again"}
        </button>
        <button
          data-testid="back-to-practice-btn"
          onClick={() => navigate("/practice")}
          className="flex-1 flex items-center justify-center gap-2 panel rounded-sm py-4 font-bold uppercase tracking-wide text-white hover:border-[#007AFF] transition-colors"
        >
          <Home className="w-5 h-5" /> Back to Practice
        </button>
      </div>
    </div>
  );
}

const BB_MAX_INNING = 9;

function PracticeBaseball({ session, setSession, navigate }) {
  const pid = session.player_id;
  const [innings, setInnings] = useState(session.legs || []);
  const [inningNumber, setInningNumber] = useState((session.legs || []).length + 1);
  const [darts, setDarts] = useState([]);
  const [burst, setBurst] = useState(null);

  const done = inningNumber > BB_MAX_INNING;
  const target = inningNumber;
  const runsThisTurn = darts.reduce((s, d) => s + d.mult, 0);
  const totalRuns =
    innings.reduce((s, i) => s + ((i.turns || [])[0]?.runs || 0), 0) + (done ? 0 : runsThisTurn);
  const best = session.personal_best || 0;

  const persist = async (legs, complete) => {
    try {
      const { data } = await api.put(`/practice/${session.id}`, {
        legs,
        ...(complete ? { status: "completed" } : {}),
      });
      if (complete) setSession(data);
    } catch {
      toast.error("Could not save. Check connection.");
    }
  };

  const commitInning = (finalDarts) => {
    const runs = finalDarts.reduce((s, d) => s + d.mult, 0);
    const inning = {
      leg_number: inningNumber,
      inning: inningNumber,
      target,
      turns: [{ player_id: pid, darts: finalDarts, runs }],
    };
    const newInnings = [...innings, inning];
    setInnings(newInnings);
    setDarts([]);
    if (inningNumber >= BB_MAX_INNING) {
      setBurst({ label: "GAME COMPLETE", sub: `${newInnings.reduce((s, i) => s + i.turns[0].runs, 0)} runs`, color: "#FFCC00" });
      playCheer("checkout");
      persist(newInnings, true);
    } else {
      persist(newInnings, false);
      setInningNumber((n) => n + 1);
    }
  };

  const throwDart = (mult) => {
    if (done) return;
    const newDarts = [...darts, { mult }];
    if (newDarts.length === 3) {
      commitInning(newDarts);
      return;
    }
    setDarts(newDarts);
  };

  const undo = () => {
    if (darts.length > 0) {
      setDarts(darts.slice(0, -1));
    } else if (innings.length > 0 && inningNumber > innings.length) {
      // step back into previous inning
      const last = innings[innings.length - 1];
      setInnings(innings.slice(0, -1));
      setInningNumber(last.inning || last.leg_number);
      setDarts(last.turns[0].darts);
    }
  };

  return (
    <div className="max-w-xl mx-auto">
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#34C759]">
            Practice · Baseball
          </div>
          <div className="font-display text-3xl font-black uppercase text-white leading-none">
            Inning {Math.min(inningNumber, BB_MAX_INNING)} of {BB_MAX_INNING}
          </div>
        </div>
        <button
          data-testid="bb-exit-btn"
          onClick={() => navigate("/practice")}
          className="flex items-center gap-1.5 text-sm font-bold uppercase text-slate-400 hover:text-[#FF3B30] transition-colors"
        >
          <Flag className="w-4 h-4" /> Exit
        </button>
      </div>

      <div className="panel rounded-sm p-6 mb-4 text-center border-t-2 border-t-[#34C759]">
        <div className="flex items-center justify-center gap-2 mb-1">
          <Avatar name={session.player_name} size={28} active />
          <span className="text-sm font-bold uppercase text-slate-200">{session.player_name}</span>
        </div>
        <div className="text-[10px] font-bold uppercase tracking-[0.15em] text-slate-500">Target</div>
        <div data-testid="bb-prac-target" className="font-display text-8xl font-black text-white leading-none">
          {target}
        </div>
        <div data-testid="bb-coach-tip" className="mt-1 text-sm font-bold uppercase tracking-wide text-[#FFCC00]">
          🎯 Aim: T{target} · Triple = 3 runs
        </div>
      </div>

      <div className="grid grid-cols-3 gap-1 mb-4">
        <StatTile testid="bb-score" label="Score" value={`${totalRuns} / 81`} accent="text-[#FFCC00]" />
        <StatTile testid="bb-inning-runs" label="This Inning" value={runsThisTurn} accent="text-[#34C759]" />
        <StatTile testid="bb-best" label="Personal Best" value={best || "—"} accent="text-[#007AFF]" />
      </div>

      <div className="flex items-center justify-center gap-2 mb-3 h-10">
        {[0, 1, 2].map((i) => {
          const d = darts[i];
          const lbl = d ? (d.mult === 0 ? "MISS" : `+${d.mult}`) : "—";
          return (
            <div
              key={i}
              className={`min-w-[64px] px-3 py-1.5 rounded-sm text-center font-display text-lg font-bold border ${
                d ? (d.mult > 0 ? "border-[#34C759] text-[#34C759]" : "border-[#FF3B30] text-[#FF3B30]") : "border-[#262626] text-slate-700"
              }`}
            >
              {lbl}
            </div>
          );
        })}
      </div>

      <div className="space-y-1">
        <div className="grid grid-cols-3 gap-1">
          <button data-testid="bb-single-btn" onClick={() => throwDart(1)} className="py-6 rounded-sm font-display text-2xl font-black uppercase tracking-wider bg-[#34C759] hover:bg-[#2fb350] text-black transition-colors duration-100">
            Single<span className="block text-xs font-bold opacity-70">+1</span>
          </button>
          <button data-testid="bb-double-btn" onClick={() => throwDart(2)} className="py-6 rounded-sm font-display text-2xl font-black uppercase tracking-wider bg-[#007AFF] hover:bg-[#0069d9] text-white transition-colors duration-100">
            Double<span className="block text-xs font-bold opacity-70">+2</span>
          </button>
          <button data-testid="bb-triple-btn" onClick={() => throwDart(3)} className="py-6 rounded-sm font-display text-2xl font-black uppercase tracking-wider bg-[#FF3B30] hover:bg-[#e0332a] text-white transition-colors duration-100">
            Triple<span className="block text-xs font-bold opacity-70">+3</span>
          </button>
        </div>
        <button data-testid="bb-miss-btn" onClick={() => throwDart(0)} className="w-full py-4 rounded-sm font-display text-2xl font-black uppercase tracking-wider panel text-slate-300 hover:bg-[#1f1f1f] transition-colors duration-100">
          Miss <span className="text-sm opacity-70">+0</span>
        </button>
        <button data-testid="bb-undo-btn" onClick={undo} className="w-full py-2.5 rounded-sm font-bold uppercase text-xs tracking-wide panel text-slate-300 hover:text-[#FF3B30] flex items-center justify-center gap-1">
          <Undo2 className="w-4 h-4" /> Undo Dart
        </button>
      </div>

      <div className="mt-4 panel rounded-sm p-3">
        <div className="text-[10px] font-bold uppercase tracking-[0.15em] text-slate-500 mb-2">Runs by inning</div>
        <div className="flex flex-wrap gap-1">
          {innings.map((inn) => (
            <div key={inn.inning} className="w-10 text-center">
              <div className="text-[10px] text-slate-500">{inn.inning}</div>
              <div className="font-display text-lg font-bold text-white">{inn.turns[0].runs}</div>
            </div>
          ))}
          {!done && (
            <div className="w-10 text-center">
              <div className="text-[10px] text-[#FFCC00]">{inningNumber}</div>
              <div className="font-display text-lg font-bold text-[#FFCC00]">{runsThisTurn}</div>
            </div>
          )}
        </div>
      </div>

      <AnimatePresence>
        {burst && <Burst label={burst.label} sub={burst.sub} color={burst.color} onDone={() => setBurst(null)} />}
      </AnimatePresence>
    </div>
  );
}

function PracticeSummaryBaseball({ session, navigate }) {
  const s = session.stats || {};
  const rows = [
    ["Score", `${s.total_runs ?? 0} / 81`],
    ["Runs per inning", s.avg_runs_per_inning ?? 0],
    ["Innings played", s.innings ?? 0],
  ];
  return (
    <div className="max-w-2xl mx-auto">
      <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} className="text-center py-6">
        <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-[#34C759] text-black font-bold uppercase text-xs tracking-widest rounded-sm mb-4">
          Baseball Practice Complete
        </div>
        <h1 className="font-display text-6xl font-black uppercase tracking-tighter text-white leading-none">
          {session.player_name}
        </h1>
        <div data-testid="bb-final-score" className="font-display text-3xl font-bold text-[#FFCC00] mt-2">
          {s.total_runs ?? 0} / 81 runs
        </div>
      </motion.div>

      <AchievementBanner achievement={session.achievement} unit=" runs" />

      <div className="panel rounded-sm p-6">
        <div className="space-y-1">
          {rows.map(([k, v]) => (
            <div key={k} className="flex items-center justify-between text-sm border-b border-[#1c1c1c] py-1.5">
              <span className="text-slate-400 font-medium">{k}</span>
              <span className="font-display text-lg font-bold text-white">{v}</span>
            </div>
          ))}
        </div>
      </div>

      <div className="flex flex-col sm:flex-row gap-2 mt-6">
        <button data-testid="practice-again-btn" onClick={() => navigate("/practice")} className="flex-1 bg-[#34C759] hover:bg-[#2fb350] text-black font-bold uppercase tracking-wide py-4 rounded-sm transition-colors">
          Practice Again
        </button>
        <button data-testid="practice-home-btn" onClick={() => navigate("/")} className="flex-1 flex items-center justify-center gap-2 panel rounded-sm py-4 font-bold uppercase tracking-wide text-white hover:border-[#007AFF] transition-colors">
          <Home className="w-5 h-5" /> Return Home
        </button>
      </div>
    </div>
  );
}

function PracticeATW({ session, setSession, navigate }) {
  const pid = session.player_id;
  const [completedLegs, setCompletedLegs] = useState(session.legs || []);
  const [roundNumber, setRoundNumber] = useState((session.legs || []).length + 1);
  const [progress, setProgress] = useState(0);
  const [darts, setDarts] = useState([]);
  const [turns, setTurns] = useState([]);
  const [startedAt, setStartedAt] = useState(Date.now());
  const [burst, setBurst] = useState(null);

  const stats = useMemo(() => atwStats(completedLegs, turns, darts), [completedLegs, turns, darts]);

  const persist = async (legs) => {
    try {
      await api.put(`/practice/${session.id}`, { legs });
    } catch {}
  };

  const finishRound = (dartsThrown) => {
    const turn = { player_id: pid, darts: dartsThrown, hits: dartsThrown.filter((d) => d.hit).length, darts_used: dartsThrown.length };
    const leg = {
      leg_number: roundNumber,
      starting_player_id: pid,
      winner_id: pid,
      turns: [...turns, turn],
      duration_ms: Date.now() - startedAt,
      atw: true,
    };
    const allLegs = [...completedLegs, leg];
    setCompletedLegs(allLegs);
    persist(allLegs);
    setBurst({ label: "ROUND", sub: "COMPLETE", color: "#34C759" });
    playCheer("checkout");
    setTurns([]);
    setDarts([]);
    setProgress(0);
    setStartedAt(Date.now());
    setRoundNumber((n) => n + 1);
  };

  const endTurn = (dartsThrown, progressVal) => {
    const turn = { player_id: pid, darts: dartsThrown, hits: dartsThrown.filter((d) => d.hit).length, darts_used: dartsThrown.length };
    setTurns([...turns, turn]);
    setDarts([]);
    setProgress(progressVal);
  };

  const throwDart = (mult) => {
    const hit = mult > 0;
    const dart = { target: ATW_TARGETS[progress], hit, mult };
    const newDarts = [...darts, dart];
    const newProgress = hit ? progress + mult : progress;
    if (hit && newProgress >= ATW_TARGETS.length) {
      finishRound(newDarts);
      return;
    }
    if (newDarts.length === 3) {
      endTurn(newDarts, newProgress);
      return;
    }
    setDarts(newDarts);
    setProgress(newProgress);
  };

  const undo = () => {
    if (darts.length === 0) return;
    const last = darts[darts.length - 1];
    setProgress(last.hit ? progress - (last.mult || 1) : progress);
    setDarts(darts.slice(0, -1));
  };

  const endSession = async () => {
    if (!window.confirm("End this practice session?")) return;
    const { data } = await api.put(`/practice/${session.id}`, { legs: completedLegs, status: "completed" });
    setSession(data);
  };

  const targetLabel = (idx) => (idx >= ATW_TARGETS.length ? "DONE" : `${ATW_TARGETS[idx]}`);
  const pct = Math.round((progress / ATW_TARGETS.length) * 100);

  return (
    <div className="max-w-3xl mx-auto">
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#34C759]">
            Practice · Around the World
          </div>
          <div className="font-display text-3xl font-black uppercase text-white leading-none">
            Round {roundNumber}
          </div>
        </div>
        <button
          data-testid="end-practice-btn"
          onClick={endSession}
          className="flex items-center gap-1.5 text-sm font-bold uppercase text-slate-400 hover:text-[#FF3B30] transition-colors"
        >
          <Flag className="w-4 h-4" /> End Session
        </button>
      </div>

      <div className="panel rounded-sm p-6 mb-4 text-center border-t-2 border-t-[#34C759]">
        <div className="flex items-center justify-center gap-2 mb-1">
          <Avatar name={session.player_name} size={28} active />
          <span className="text-sm font-bold uppercase text-slate-200">{session.player_name}</span>
        </div>
        <div className="text-[10px] font-bold uppercase tracking-[0.15em] text-slate-500">On target</div>
        <div className="font-display text-7xl md:text-8xl font-black text-white leading-none">
          {targetLabel(progress)}
        </div>
        <div className="mt-3 max-w-xs mx-auto h-1.5 bg-[#262626] rounded-full overflow-hidden">
          <div className="h-full bg-[#34C759]" style={{ width: `${pct}%` }} />
        </div>
        <div className="text-[10px] font-bold text-slate-500 mt-1">{progress}/{ATW_TARGETS.length}</div>
      </div>

      <div className="grid grid-cols-3 gap-1 mb-4">
        <StatTile testid="p-hitpct" label="Hit %" value={`${stats.hitPct}%`} accent="text-[#007AFF]" />
        <StatTile testid="p-rounds" label="Rounds" value={stats.rounds} />
        <StatTile testid="p-darts" label="Darts" value={stats.darts} accent="text-[#FFCC00]" />
      </div>

      <div className="flex items-center justify-center gap-2 mb-3 h-9">
        {[0, 1, 2].map((i) => {
          const d = darts[i];
          return (
            <div
              key={i}
              className={`min-w-[72px] px-3 py-1.5 rounded-sm text-center font-display text-lg font-bold border ${
                d
                  ? d.hit
                    ? "border-[#34C759] text-[#34C759]"
                    : "border-[#FF3B30] text-[#FF3B30]"
                  : "border-[#262626] text-slate-700"
              }`}
            >
              {d ? `${d.target}${d.hit && d.mult > 1 ? `×${d.mult}` : ""}${d.hit ? " ✓" : " ✗"}` : "—"}
            </div>
          );
        })}
      </div>

      <div className="max-w-lg mx-auto space-y-1">
        <div className="grid grid-cols-3 gap-1">
          <button
            data-testid="atw-single-btn"
            onClick={() => throwDart(1)}
            className="py-6 rounded-sm font-display text-2xl font-black uppercase tracking-wider bg-[#34C759] hover:bg-[#2fb350] text-black transition-colors duration-100"
          >
            Single
          </button>
          <button
            data-testid="atw-double-btn"
            onClick={() => throwDart(2)}
            className="py-6 rounded-sm font-display text-2xl font-black uppercase tracking-wider bg-[#007AFF] hover:bg-[#0069d9] text-white transition-colors duration-100"
          >
            Double
          </button>
          <button
            data-testid="atw-treble-btn"
            onClick={() => throwDart(3)}
            className="py-6 rounded-sm font-display text-2xl font-black uppercase tracking-wider bg-[#FF3B30] hover:bg-[#e0332a] text-white transition-colors duration-100"
          >
            Treble
          </button>
        </div>
        <button
          data-testid="atw-miss-btn"
          onClick={() => throwDart(0)}
          className="w-full py-4 rounded-sm font-display text-2xl font-black uppercase tracking-wider panel text-slate-300 hover:bg-[#1f1f1f] transition-colors duration-100"
        >
          Miss
        </button>
        <button
          data-testid="atw-undo-btn"
          onClick={undo}
          className="w-full py-2.5 rounded-sm font-bold uppercase text-xs tracking-wide panel text-slate-300 hover:text-[#FF3B30] flex items-center justify-center gap-1"
        >
          <Undo2 className="w-4 h-4" /> Undo Dart
        </button>
      </div>

      <AnimatePresence>
        {burst && (
          <Burst label={burst.label} sub={burst.sub} color={burst.color} onDone={() => setBurst(null)} />
        )}
      </AnimatePresence>
    </div>
  );
}

function atwStats(legs, currentTurns, currentDarts) {
  let darts = 0, hits = 0;
  const tally = (t) => {
    (t.darts || []).forEach((d) => {
      darts += 1;
      if (d.hit) hits += 1;
    });
  };
  legs.forEach((l) => (l.turns || []).forEach(tally));
  currentTurns.forEach(tally);
  currentDarts.forEach((d) => {
    darts += 1;
    if (d.hit) hits += 1;
  });
  return {
    darts,
    hits,
    rounds: legs.length,
    hitPct: darts ? Math.round((hits / darts) * 100) : 0,
  };
}

function PracticeSummaryATW({ session, navigate }) {
  const s = session.stats || {};
  const rows = [
    ["Rounds Completed", session.legs?.length || 0],
    ["Hit %", `${s.hit_pct ?? 0}%`],
    ["Total Hits", s.total_hits ?? 0],
    ["Total Darts", s.total_darts ?? 0],
    ["Darts / Target", s.avg_darts_per_target ?? 0],
    ["Fastest Round", s.fastest_round_ms ? `${(s.fastest_round_ms / 1000).toFixed(1)}s` : "—"],
  ];
  return (
    <div className="max-w-2xl mx-auto">
      <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} className="text-center py-6">
        <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-[#34C759] text-black font-bold uppercase text-xs tracking-widest rounded-sm mb-4">
          Practice Complete
        </div>
        <h1 className="font-display text-6xl font-black uppercase tracking-tighter text-white leading-none">
          {session.player_name}
        </h1>
        <div className="font-display text-2xl font-bold text-[#007AFF] mt-2">
          {s.hit_pct ?? 0}% hit · {session.legs?.length || 0} rounds
        </div>
      </motion.div>

      <AchievementBanner achievement={session.achievement} unit="%" />

      <div className="panel rounded-sm p-6">
        <div className="space-y-1">
          {rows.map(([k, v]) => (
            <div key={k} className="flex items-center justify-between text-sm border-b border-[#1c1c1c] py-1.5">
              <span className="text-slate-400 font-medium">{k}</span>
              <span className="font-display text-lg font-bold text-white">{v}</span>
            </div>
          ))}
        </div>
      </div>

      <div className="flex flex-col sm:flex-row gap-2 mt-6">
        <button
          data-testid="practice-again-btn"
          onClick={() => navigate("/practice")}
          className="flex-1 bg-[#34C759] hover:bg-[#2fb350] text-black font-bold uppercase tracking-wide py-4 rounded-sm transition-colors"
        >
          Practice Again
        </button>
        <button
          data-testid="practice-home-btn"
          onClick={() => navigate("/")}
          className="flex-1 flex items-center justify-center gap-2 panel rounded-sm py-4 font-bold uppercase tracking-wide text-white hover:border-[#007AFF] transition-colors"
        >
          <Home className="w-5 h-5" /> Return Home
        </button>
      </div>
    </div>
  );
}

function PracticeBoard({ session, setSession, navigate }) {
  const pid = session.player_id;
  const [completedLegs, setCompletedLegs] = useState(session.legs || []);
  const [legNumber, setLegNumber] = useState((session.legs || []).length + 1);
  const [remaining, setRemaining] = useState(START_501);
  const [darts, setDarts] = useState([]);
  const [turns, setTurns] = useState([]);
  const [mult, setMult] = useState(1);
  const [burst, setBurst] = useState(null);
  const [started, setStarted] = useState(false);
  const [coachOn, setCoachOn] = useState(true);
  const finishRule = session.finish_rule || "double_out";
  const startRule = session.start_rule || "straight_in";

  const stats = useMemo(() => sessionStats(completedLegs, turns), [completedLegs, turns]);
  const used = darts.reduce((s, d) => s + (d.counted ? d.value : 0), 0);
  const live = remaining - used;
  const suggestion = started ? checkoutSuggestion(live, finishRule) : "";
  const tip = coachOn && started ? coachTip(live, 3 - darts.length, finishRule) : null;
  const needsDoubleIn = startRule === "double_in" && !started;

  const persist = async (legs) => {
    try {
      await api.put(`/practice/${session.id}`, { legs });
    } catch {}
  };

  const scoreOf = (ds) => ds.reduce((s, d) => s + (d.counted ? d.value : 0), 0);

  const handleDart = (base, forceMult) => {
    const m = forceMult ? forceMult : base === 25 && mult === 3 ? 2 : mult;
    const dart = { base, multiplier: m, value: base * m, label: dartLabel(base, m) };
    const usedCounted = darts.reduce((s, d) => s + (d.counted ? d.value : 0), 0);
    const res = resolveDart(remaining, usedCounted, dart, finishRule, started, startRule);
    dart.counted = res.counted;
    const newDarts = [...darts, dart];
    setMult(1);
    if (res.started && !started) setStarted(true);

    if (res.status === "win") {
      const score = scoreOf(newDarts);
      const turn = {
        player_id: pid, darts: newDarts, turn_score: score,
        remaining_before: remaining, remaining_after: 0,
        busted: false, is_checkout: true, darts_used: newDarts.length,
      };
      const leg = { leg_number: legNumber, starting_player_id: pid, winner_id: pid, turns: [...turns, turn] };
      const allLegs = [...completedLegs, leg];
      setCompletedLegs(allLegs);
      persist(allLegs);
      setBurst({ label: "CHECKOUT", sub: `${score}`, color: "#FFCC00" });
      playCheer("checkout");
      setTurns([]);
      setDarts([]);
      setRemaining(START_501);
      setStarted(false);
      setLegNumber((n) => n + 1);
      return;
    }
    if (res.status === "bust") {
      const turn = {
        player_id: pid, darts: newDarts, turn_score: 0,
        remaining_before: remaining, remaining_after: remaining,
        busted: true, is_checkout: false, darts_used: newDarts.length,
      };
      setTurns([...turns, turn]);
      setDarts([]);
      return;
    }
    // 'ok' or 'nostart'
    if (newDarts.length === 3) {
      const score = scoreOf(newDarts);
      const turn = {
        player_id: pid, darts: newDarts, turn_score: score,
        remaining_before: remaining, remaining_after: remaining - score,
        busted: false, is_checkout: false, darts_used: 3,
      };
      if (score === 180) {
        setBurst({ label: "180", color: "#FF3B30" });
        playCheer("180");
      }
      setTurns([...turns, turn]);
      setRemaining(remaining - score);
      setDarts([]);
      return;
    }
    setDarts(newDarts);
  };

  const undo = () => setDarts(darts.slice(0, -1));

  const lastVisit = (() => {
    const all = [];
    completedLegs.forEach((l) => (l.turns || []).forEach((t) => all.push(t)));
    turns.forEach((t) => all.push(t));
    return all.length ? all[all.length - 1] : null;
  })();
  const visitDartLabel = (d) => {
    if (!d) return "—";
    if (d.base === 0) return "MISS";
    if (d.base === 25) return d.multiplier === 2 ? "BULL" : "25";
    return (d.multiplier === 3 ? "T" : d.multiplier === 2 ? "D" : "") + d.base;
  };

  const endSession = async () => {
    if (!window.confirm("End this practice session?")) return;
    const { data } = await api.put(`/practice/${session.id}`, {
      legs: completedLegs,
      status: "completed",
    });
    setSession(data);
  };

  return (
    <div className="max-w-3xl mx-auto">
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#34C759]">
            Practice · 501 · {startRule === "double_in" ? "Double In" : "Straight In"} /{" "}
            {finishRule === "double_out" ? "Double Out" : "Straight Out"}
          </div>
          <div className="font-display text-3xl font-black uppercase text-white leading-none">
            Leg {legNumber}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            data-testid="coach-toggle-btn"
            onClick={() => setCoachOn((v) => !v)}
            className={`flex items-center gap-1.5 text-xs font-bold uppercase px-2.5 py-1.5 rounded-sm transition-colors ${
              coachOn ? "bg-[#FFCC00] text-black" : "panel text-slate-400 hover:text-white"
            }`}
          >
            <Target className="w-3.5 h-3.5" /> Coach {coachOn ? "On" : "Off"}
          </button>
          <button
            data-testid="end-practice-btn"
            onClick={endSession}
            className="flex items-center gap-1.5 text-sm font-bold uppercase text-slate-400 hover:text-[#FF3B30] transition-colors"
          >
            <Flag className="w-4 h-4" /> End Session
          </button>
        </div>
      </div>

      <div className="panel rounded-sm p-6 mb-4 text-center border-t-2 border-t-[#34C759]">
        <div className="flex items-center justify-center gap-2 mb-1">
          <Avatar name={session.player_name} size={28} active />
          <span className="text-sm font-bold uppercase text-slate-200">{session.player_name}</span>
        </div>
        <div className="font-display text-7xl md:text-8xl font-black text-white leading-none">
          {live}
        </div>
        {suggestion && (
          <div className="text-[#FFCC00] text-sm font-bold uppercase tracking-wide mt-1">
            {suggestion}
          </div>
        )}
        {tip && (
          <div data-testid="coach-tip" className="mt-2 inline-flex flex-col items-center gap-0.5 px-4 py-2 rounded-sm bg-[#1a1a1a] border border-[#FFCC00]/40">
            <div className="font-display text-2xl font-black text-[#FFCC00]">🎯 {tip.aim}</div>
            <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">{tip.note}</div>
          </div>
        )}
        {needsDoubleIn && (
          <div className="text-[#FF9500] text-sm font-bold uppercase tracking-wide mt-1">
            Hit a double to start
          </div>
        )}
      </div>

      <div className="grid grid-cols-4 gap-1 mb-4">
        <StatTile testid="p-avg" label="Average" value={stats.avg} accent="text-[#007AFF]" />
        <StatTile testid="p-legs" label="Legs" value={stats.legs} />
        <StatTile testid="p-180" label="180s" value={stats.c180} accent="text-[#FF3B30]" />
        <StatTile testid="p-best" label="Best Leg" value={stats.bestLeg || "—"} accent="text-[#FFCC00]" />
      </div>

      <div className="panel rounded-sm p-3 mb-4" data-testid="practice-last-visit">
        <div className="text-[9px] font-bold uppercase tracking-[0.2em] text-slate-600 mb-1">Last Visit</div>
        <div className="flex items-center justify-center gap-1.5">
          {[0, 1, 2].map((i) => (
            <span key={i} data-testid={`practice-last-visit-dart-${i}`} className="min-w-[44px] px-1.5 py-0.5 rounded-sm bg-[#141414] border border-[#262626] font-display text-sm font-bold text-slate-300 text-center">
              {lastVisit ? visitDartLabel(lastVisit.darts?.[i]) : "—"}
            </span>
          ))}
          <span data-testid="practice-last-visit-total" className={`ml-1.5 font-display text-xl font-black ${lastVisit && lastVisit.busted ? "text-[#FF3B30]" : "text-white"}`}>
            {lastVisit ? (lastVisit.busted ? "BUST" : lastVisit.turn_score) : 0}
          </span>
        </div>
      </div>

      <div className="flex items-center justify-center gap-2 mb-3 h-9">
        {[0, 1, 2].map((i) => {
          const d = darts[i];
          return (
            <div
              key={i}
              className={`min-w-[64px] px-3 py-1.5 rounded-sm text-center font-display text-lg font-bold border ${
                d ? "border-[#007AFF] text-white" : "border-[#262626] text-slate-700"
              }`}
            >
              {d ? d.label : "—"}
            </div>
          );
        })}
      </div>

      <DartPad mult={mult} setMult={setMult} onDart={handleDart} onUndo={undo} />

      <AnimatePresence>
        {burst && (
          <Burst label={burst.label} sub={burst.sub} color={burst.color} onDone={() => setBurst(null)} />
        )}
      </AnimatePresence>
    </div>
  );
}

function PracticeSummary({ session, navigate }) {
  const s = session.stats || {};
  const rows = [
    ["3-Dart Average", s.three_dart_avg],
    ["First 9 Avg", s.first9_avg],
    ["Highest Score", s.highest_turn],
    ["100+", s.count_100],
    ["140+", s.count_140],
    ["180s", s.count_180],
    ["Highest Checkout", s.highest_checkout],
    ["Best Leg (darts)", s.best_winning_leg || "—"],
    ["Total Darts", s.total_darts],
  ];
  return (
    <div className="max-w-2xl mx-auto">
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="text-center py-6"
      >
        <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-[#34C759] text-black font-bold uppercase text-xs tracking-widest rounded-sm mb-4">
          Practice Complete
        </div>
        <h1 className="font-display text-6xl font-black uppercase tracking-tighter text-white leading-none">
          {session.player_name}
        </h1>
        <div className="font-display text-2xl font-bold text-[#007AFF] mt-2">
          {s.three_dart_avg} avg · {session.legs?.length || 0} legs
        </div>
      </motion.div>

      <AchievementBanner achievement={session.achievement} unit="" />

      <div className="panel rounded-sm p-6">
        <div className="space-y-1">
          {rows.map(([k, v]) => (
            <div key={k} className="flex items-center justify-between text-sm border-b border-[#1c1c1c] py-1.5">
              <span className="text-slate-400 font-medium">{k}</span>
              <span className="font-display text-lg font-bold text-white">{v ?? 0}</span>
            </div>
          ))}
        </div>
      </div>

      <div className="flex flex-col sm:flex-row gap-2 mt-6">
        <button
          data-testid="practice-again-btn"
          onClick={() => navigate("/practice")}
          className="flex-1 bg-[#34C759] hover:bg-[#2fb350] text-black font-bold uppercase tracking-wide py-4 rounded-sm transition-colors"
        >
          Practice Again
        </button>
        <button
          data-testid="practice-home-btn"
          onClick={() => navigate("/")}
          className="flex-1 flex items-center justify-center gap-2 panel rounded-sm py-4 font-bold uppercase tracking-wide text-white hover:border-[#007AFF] transition-colors"
        >
          <Home className="w-5 h-5" /> Return Home
        </button>
      </div>
    </div>
  );
}


function AchievementBanner({ achievement, unit = "" }) {
  const [burst, setBurst] = useState(null);
  useEffect(() => {
    if (achievement?.is_personal_best) {
      setBurst({ label: "PERSONAL BEST", sub: `${achievement.value}${unit}`, color: "#FFCC00" });
      playCheer("checkout");
    }
  }, [achievement, unit]);

  if (!achievement) return null;
  const { is_personal_best, previous_best, goal, goal_met, value } = achievement;
  return (
    <>
      <div className="space-y-2 mb-4">
        {is_personal_best && (
          <div
            data-testid="pb-banner"
            className="flex items-center justify-center gap-2 px-4 py-3 rounded-sm bg-[#2a2200] border border-[#FFCC00] text-[#FFCC00] font-bold uppercase tracking-wide text-sm"
          >
            <Trophy className="w-4 h-4" /> New Personal Best! {value}
            {unit}
            {previous_best > 0 && (
              <span className="text-slate-400 normal-case font-medium lowercase">
                (was {previous_best}
                {unit})
              </span>
            )}
          </div>
        )}
        {goal != null && (
          <div
            data-testid="goal-banner"
            className={`flex items-center justify-center gap-2 px-4 py-2.5 rounded-sm text-sm font-bold uppercase tracking-wide ${
              goal_met
                ? "bg-[#0d1f12] border border-[#1c5c33] text-[#34C759]"
                : "panel text-slate-400"
            }`}
          >
            {goal_met ? <CheckCircle2 className="w-4 h-4" /> : <Target className="w-4 h-4" />}
            Goal {goal}
            {unit} — {goal_met ? "Smashed it!" : "Not this time"}
          </div>
        )}
      </div>
      <AnimatePresence>
        {burst && (
          <Burst label={burst.label} sub={burst.sub} color={burst.color} onDone={() => setBurst(null)} />
        )}
      </AnimatePresence>
    </>
  );
}
